import { useMemo } from 'react'
import { AlertTriangle } from 'lucide-react'
import { useOpsCenterStore } from '../store'
import { workflowToDsl } from '../workflowToDsl'
import { SequenceDiagram } from '@features/dashboards/components/ClientSequenceDiagram/SequenceDiagram'
import { parseDsl } from '@features/dashboards/components/ClientSequenceDiagram/dsl-parser'

export interface SequenceDiagramViewProps {
  workflowId: string
}

// Renders the workflow's async webhook execution flow as a sequence diagram,
// derived live from the store's task tree (workflow -> DSL -> parsed flow ->
// existing ClientSequenceDiagram renderer). The raw DSL is also exposed below
// the diagram so it can be inspected/copied.
export function SequenceDiagramView({ workflowId }: SequenceDiagramViewProps) {
  const workflow = useOpsCenterStore((s) => s.workflows.find((w) => w.id === workflowId))

  const dsl = useMemo(() => (workflow ? workflowToDsl(workflow) : ''), [workflow])

  const flow = useMemo(() => {
    if (!dsl) return null
    const result = parseDsl(workflowId, dsl)
    return result.flow.participants.length > 0 ? result.flow : null
  }, [workflowId, dsl])

  if (!workflow) {
    return (
      <div className="flex-1 min-w-0 rounded-lg border border-[#1e2c44] bg-[#0a1120] h-[640px] grid place-items-center">
        <p className="text-xs text-[#5b6b85]">Workflow not found.</p>
      </div>
    )
  }

  return (
    <div className="flex-1 min-w-0 flex flex-col gap-3 h-[640px]">
      <div className="flex-1 min-h-0 rounded-lg border border-[#1e2c44] bg-[#0a1120] overflow-auto p-4">
        {flow ? (
          <SequenceDiagram flow={flow} />
        ) : (
          <div className="h-full grid place-items-center">
            <p className="flex items-center gap-2 text-xs text-[#5b6b85]">
              <AlertTriangle size={13} /> Couldn't render a diagram from this workflow yet.
            </p>
          </div>
        )}
      </div>

      <details className="rounded-lg border border-[#1e2c44] bg-[#0f1729] shrink-0">
        <summary className="cursor-pointer select-none px-4 py-2.5 text-xs font-medium text-[#94a3b8] hover:text-[var(--text-primary)] transition-colors">
          View generated DSL
        </summary>
        <pre className="px-4 pb-4 text-[11px] leading-relaxed font-mono text-[#94a3b8] whitespace-pre-wrap break-all overflow-y-auto max-h-64">
          {dsl}
        </pre>
      </details>
    </div>
  )
}
